import { useCallback, useMemo, useRef, useState } from 'react';

import type { ListQuery } from '@/shared/api/transport';
import type { GridPaging } from '@/shared/ui/DataGrid';

/* The screen half of the paged tenancy reads.
 *
 * The service pages every one of them, but almost nobody needs it to. A
 * deployment with four tenants and a dozen grants gets its whole answer in the
 * first request, and a table that switched to service-side search and gave up
 * sortable headers for the privilege would be worse at that size, not better.
 * So the mode is decided by the answer rather than by the screen: ask for a
 * capped page, and if the whole thing came back, hand it to the grid the way
 * every other table in the cockpit is handed its rows.
 *
 * The switch is one-way. Once an answer has arrived that did not fit, the screen
 * stays in service-side mode for as long as it is open — because the search box
 * changes hands at that boundary, and a screen that handed it back mid-session
 * would blank the term the operator had typed and then show rows still narrowed
 * by it.
 */

/** Rows per request.
 *
 * Matches `page_size_max` on the service, which clamps anything larger. Asking
 * for exactly the ceiling is what makes the single-request case as wide as it
 * can be: every deployment under a hundred tenants never pages at all. */
export const PAGE_CAP = 100;

export interface ListPaging {
  /** What to ask for. Goes straight to the query hook. */
  readonly query: ListQuery;
  /** The term the service is matching on, and the way to change it. The grid
      takes these through `gridFor`; a screen that narrows a list with something
      other than a grid — the tenant choosers do — reads them directly. */
  readonly term: string;
  readonly onTerm: (term: string) => void;
  /** The grid's paging contract, or `undefined` while the whole answer fits in
      one page — in which case the grid keeps its own search, filters and sort.
      Pass the page that came back; `undefined` while it is still in flight. */
  gridFor(page: { readonly total: number; readonly rows: readonly unknown[] } | undefined):
    | GridPaging
    | undefined;
}

export function useListPaging(limit = PAGE_CAP): ListPaging {
  const [offset, setOffset] = useState(0);
  const [term, setTerm] = useState('');
  /* One-way, and a ref rather than state because flipping it is not a reason to
     render: the render that reads a too-large `total` is already the one that
     will draw the pager. See the note above on why it never flips back. */
  const capped = useRef(false);

  const trimmed = term.trim();
  const query = useMemo<ListQuery>(
    () => ({ search: trimmed === '' ? undefined : trimmed, offset, limit }),
    [trimmed, offset, limit],
  );

  /* Narrowing returns to the first page. Typing while standing on page four of
     the old answer would land on page four of the new one, which is usually
     past its end and always somewhere nobody asked to be. */
  const onTerm = useCallback((next: string) => {
    setTerm(next);
    setOffset(0);
  }, []);

  function gridFor(
    page: { readonly total: number; readonly rows: readonly unknown[] } | undefined,
  ): GridPaging | undefined {
    if (page && page.total > page.rows.length) capped.current = true;
    if (page === undefined || !capped.current) return undefined;
    return {
      total: page.total,
      offset,
      limit,
      onOffset: setOffset,
      term,
      onTerm,
    };
  }

  return { query, term, onTerm, gridFor };
}
